import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, alpha, Stack } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { FEATURE_CARD_PALETTE } from './platformFeaturesData';
import ModulePreviewDialog from './ModulePreviewDialog';

const CARD_HEIGHT = 196;

export default function FlipFeatureCard({ feature, colorIndex = 0 }) {
  const navigate = useNavigate();
  const [previewOpen, setPreviewOpen] = useState(false);
  const isComingSoon = Boolean(feature.comingSoon);

  const colors = useMemo(
    () => FEATURE_CARD_PALETTE[colorIndex % FEATURE_CARD_PALETTE.length],
    [colorIndex]
  );

  const openPreview = useCallback(() => setPreviewOpen(true), []);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPreview();
    }
  };

  const featureForDialog = useMemo(
    () => ({ ...feature, previewScreen: isComingSoon ? null : feature.id }),
    [feature, isComingSoon]
  );

  return (
    <>
      <Box
        onClick={openPreview}
        onKeyDown={onKeyDown}
        tabIndex={0}
        role="button"
        aria-pressed={previewOpen}
        aria-label={
          isComingSoon ? `${feature.title}. View roadmap` : `${feature.title}. Open live demo preview`
        }
        sx={{
          height: CARD_HEIGHT,
          cursor: 'pointer',
          outline: 'none',
          borderRadius: 2,
          filter: 'drop-shadow(0 1px 4px rgba(0,0,0,0.05))',
          transition: 'filter 0.25s ease, transform 0.25s ease',
          ...(isComingSoon && {
            background: `repeating-linear-gradient(-45deg, ${alpha(colors.icon, 0.04)}, ${alpha(colors.icon, 0.04)} 8px, transparent 8px, transparent 16px)`,
            borderRadius: 2
          }),
          '&:hover': {
            transform: 'translateY(-3px)',
            filter: 'drop-shadow(0 8px 18px rgba(0,0,0,0.1))'
          },
          '&:focus-visible': {
            boxShadow: `0 0 0 2px ${alpha(colors.icon, 0.45)}`
          }
        }}
      >
        <Card
          elevation={0}
          sx={{
            height: '100%',
            bgcolor: colors.front,
            border: '1px solid',
            borderColor: colors.border,
            color: colors.text,
            boxShadow: colors.shadow,
            display: 'flex',
            flexDirection: 'column',
            ...(isComingSoon && {
              borderStyle: 'dashed',
              borderWidth: 2
            })
          }}
        >
          <CardContent sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 1.75 } }}>
            <Box
              sx={{
                color: colors.icon,
                mb: 0.75,
                width: 36,
                height: 36,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(colors.icon, 0.12),
                boxShadow: `inset 0 1px 0 ${alpha('#fff', 0.55)}`,
                '& .MuiSvgIcon-root': { fontSize: 22 }
              }}
            >
              {feature.icon}
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.25, fontSize: '0.92rem', color: colors.text }}
            >
              {feature.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.muted,
                lineHeight: 1.45,
                flex: 1,
                fontSize: '0.78rem',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {feature.summary}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
              {isComingSoon ? (
                <>
                  <RocketLaunchIcon sx={{ fontSize: 13, color: colors.muted, opacity: 0.85 }} />
                  <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 600, fontSize: '0.68rem' }}>
                    Horizontal & vertical roadmap
                  </Typography>
                </>
              ) : (
                <>
                  <OpenInNewIcon sx={{ fontSize: 13, color: colors.muted, opacity: 0.85 }} />
                  <Typography variant="caption" sx={{ color: colors.muted, fontWeight: 600, fontSize: '0.68rem' }}>
                    Live demo preview
                  </Typography>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <ModulePreviewDialog
        feature={featureForDialog}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onSignIn={() => {
          setPreviewOpen(false);
          navigate('/login', { state: { from: { pathname: '/dashboard' }, entryLabel: 'Dashboard' } });
        }}
      />
    </>
  );
}
